import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api } from '@/lib/api';
import { queryKeys } from '@/lib/query';
import type { SshConfigResult, SshConfigState } from '@/types/api';

/**
 * Hooks für Sprint-3-SSH-Config (WT-303).
 *
 * - `useSshConfig` liest den aktuellen ssh-config-File-Inhalt + Stats
 *   (read-only Preview).
 * - `useRegenerateSshConfig` schreibt die Datei neu aus DB-Inventar.
 *   Optional dry-run (kein Write, nur Diff).
 */

export function useSshConfig(): ReturnType<typeof useQuery<SshConfigState>> {
  return useQuery<SshConfigState>({
    queryKey: queryKeys.sshConfig.state,
    queryFn: () => api.get<SshConfigState>('/ssh-config'),
  });
}

export interface RegenerateSshConfigInput {
  dryRun?: boolean;
}

export function useRegenerateSshConfig(): ReturnType<
  typeof useMutation<SshConfigResult, Error, RegenerateSshConfigInput | void>
> {
  const qc = useQueryClient();
  return useMutation<SshConfigResult, Error, RegenerateSshConfigInput | void>({
    mutationFn: (input) =>
      api.post<SshConfigResult, { dry_run?: boolean }>('/ssh-config/regenerate', {
        dry_run: input?.dryRun ?? false,
      }),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: queryKeys.sshConfig.state });
    },
  });
}
